import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { confirmDialog } from 'primereact/confirmdialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useDispatch, useSelector } from 'react-redux';
import { _fetchEnginList, _generateTagReport } from '../../../api/api';
import PageHeader from '../../../../shared/PageHeader/PageHeader';
import { setAlertParams } from '../../../../../store/slices/alert.slice';

/**
 * Composant de configuration de rapport avec PrimeFlex
 * Structure JSON: { data: {}, filter: { startDate, endDate, tags }, params: { title, srcObject }, name }
 */
const ReportConfig = ({ onBack, onGenerate }) => {
  const dispatch = useDispatch();
  const toast = React.useRef(null);
  
  // State pour les capteurs chargés depuis le backend (comme dans Courbe.tsx)
  const [capteurs, setCapteurs] = React.useState([]);
  const [loadingCapteurs, setLoadingCapteurs] = React.useState(true);

  // Note: loadingCapteurs est déjà défini plus haut
  const [generating, setGenerating] = useState(false);
  const [formData, setFormData] = useState({
    title: `Rapport du ${new Date().toLocaleDateString('fr-FR')}`,
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 jours par défaut
    endDate: new Date(),
    selectedTags: []
  });

  // Fonction pour charger les engins depuis le backend
  const loadCapteurs = async () => {
    try {
      setLoadingCapteurs(true);
      console.log('📊 Chargement des engins pour ReportConfig...');
      
      const response = await _fetchEnginList();
      console.log('📊 Réponse API engins:', response);
      
      if (response.data && response.data.success && response.data.result) {
        const enginsData = Array.isArray(response.data.result) ? response.data.result : [];
        console.log('📊 Engins data received:', enginsData.length);
        
        // Transformer les données engin en format enrichi pour MultiSelect
        const transformedSensors = enginsData
          .filter((engin) => engin.temperature !== null || engin.humidity !== null) // Garder seulement les engins avec temp/humidity
          .map((engin) => ({
            macAddr: engin.tagname || engin.macAddr || engin.uid || 'N/A',
            label: engin.title || engin.label || engin.reference || `Engin-${engin.uid}`,
            name: engin.title || engin.label || engin.reference,
            uid: engin.uid,
            id: engin.id,
            tagname: engin.tagname,
            active: engin.sysActive === 1
          }));
        
        setCapteurs(transformedSensors);
        console.log('✅ Engins chargés pour ReportConfig:', transformedSensors.length);
      } else {
        console.log('❌ Aucun engin reçu');
        setCapteurs([]);
      }
      
    } catch (error) {
      console.error('🚨 Erreur lors du chargement des engins:', error);
      setCapteurs([]);
      toast.current?.show({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible de charger la liste des engins',
        life: 3000
      });
    } finally {
      setLoadingCapteurs(false);
    }
  };

  // Charger les capteurs au montage du composant (comme dans Courbe.tsx)
  React.useEffect(() => {
    loadCapteurs();
  }, []);

  // Transformer les engins pour le MultiSelect
  const tagOptions = React.useMemo(() => {
    console.log('🔄 Transformation des engins pour MultiSelect:', capteurs);
    
    if (!capteurs || capteurs.length === 0) {
      console.log('⚠️ Pas d\'engins disponibles, utilisation de données de test');
      // Données de test si pas d'engins
      return [
        { label: 'Engin Test 1', value: 'test1', code: 'test1', uid: 'test1', id: 'test1' },
        { label: 'Engin Test 2', value: 'test2', code: 'test2', uid: 'test2', id: 'test2' },
        { label: 'Engin Test 3', value: 'test3', code: 'test3', uid: 'test3', id: 'test3' }
      ];
    }
    
    const options = capteurs.map(engin => ({
      label: engin.label || `Engin-${engin.id || engin.uid}`,
      value: engin.id || engin.uid, // Préférer id, fallback sur uid
      code: engin.id || engin.uid,
      uid: engin.uid,
      id: engin.id,
      macAddr: engin.macAddr,
      tagname: engin.tagname
    }));
    
    console.log('✅ Options générées pour MultiSelect:', options);
    return options;
  }, [capteurs]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation',
        detail: 'Veuillez saisir un titre pour le rapport',
        life: 3000
      });
      return false;
    }

    if (!formData.startDate || !formData.endDate) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation',
        detail: 'Veuillez sélectionner une période',
        life: 3000
      });
      return false;
    }

    if (formData.startDate >= formData.endDate) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation',
        detail: 'La date de début doit être antérieure à la date de fin',
        life: 3000
      });
      return false;
    }

    if (!formData.selectedTags || formData.selectedTags.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation',
        detail: 'Veuillez sélectionner au moins un capteur',
        life: 3000
      });
      return false;
    }

    return true;
  };

  const handleGenerateReport = async () => {
    if (!validateForm()) return;

    try {
      setGenerating(true);

      // Récupérer les objets complets des capteurs sélectionnés
      const selectedSensorObjects = formData.selectedTags.map(selectedId => {
        return tagOptions.find(option => option.value === selectedId);
      }).filter(Boolean); // Filtrer les undefined au cas où

      console.log('📊 Capteurs sélectionnés (objets complets):', selectedSensorObjects);

      // Construire la structure JSON demandée avec srcIds pour les engins
      const reportPayload = {
        data: {},
        filter: {
          startDate: formData.startDate.toISOString().split('T')[0],
          endDate: formData.endDate.toISOString().split('T')[0],
          tags: selectedSensorObjects.map(sensor => sensor.tagname || sensor.value).join(','),
          srcIds: selectedSensorObjects
            .map(sensor => String(sensor.uid || sensor.id || sensor.value))
            .join(','), // CSV sans espaces
          srcObject: "engin" // Spécifier que c'est un engin
        },
        params: {
          title: formData.title,
          srcObject: "sensor_repport" // Ne pas modifier
        },
        name: "tag_repport" // Ne pas modifier
      };

      console.log('Génération du rapport avec payload:', reportPayload);

      toast.current?.show({
        severity: 'info',
        summary: 'Génération',
        detail: 'Génération du rapport en cours...',
        life: 3000
      });

      // Appel direct à l'API de génération de rapport
      const response = await _generateTagReport(reportPayload);
      console.log(' Réponse de génération complète:', JSON.stringify(response, null, 2));

      // La réponse API est dans response.data
      const apiResponse = response.data || response;
      console.log(' Données API extraites:', apiResponse);

      // Vérifier différentes structures de réponse possibles
      let pdfPath = null;
      
      if (apiResponse.success && apiResponse.result) {
        // Structure 1: apiResponse.result.path
        if (apiResponse.result.path) {
          pdfPath = apiResponse.result.path;
          console.log('PDF trouvé dans result.path:', pdfPath);
        }
        // Structure 2: apiResponse.result.report_response.result[0].filepath
        else if (apiResponse.result.report_response && 
                 apiResponse.result.report_response.result && 
                 apiResponse.result.report_response.result.length > 0 && 
                 apiResponse.result.report_response.result[0].filepath) {
          pdfPath = apiResponse.result.report_response.result[0].filepath;
          console.log('PDF trouvé dans report_response.result[0].filepath:', pdfPath);
        }
        // Structure 3: apiResponse.result.report_response.detail.recordset[0].filepath
        else if (apiResponse.result.report_response && 
                 apiResponse.result.report_response.detail && 
                 apiResponse.result.report_response.detail.recordset && 
                 apiResponse.result.report_response.detail.recordset.length > 0 && 
                 apiResponse.result.report_response.detail.recordset[0].filepath) {
          pdfPath = apiResponse.result.report_response.detail.recordset[0].filepath;
          console.log(' PDF trouvé dans report_response.detail.recordset[0].filepath:', pdfPath);
        }
      }

      if (pdfPath) {
        console.log(' PDF généré avec succès:', pdfPath);

        toast.current?.show({
          severity: 'success',
          summary: 'Succès',
          detail: 'Rapport généré avec succès !',
          life: 3000
        });

        // Afficher le modal de confirmation pour ouvrir le PDF
        dispatch(setAlertParams({
          message: 'Le rapport a été généré avec succès. Voulez-vous l\'ouvrir maintenant ?',
          header: 'Rapport généré',
          icon: 'pi pi-file-pdf',
          acceptLabel: 'Oui, ouvrir',
          rejectLabel: 'Non, plus tard',
          visible: true,
          accept: () => {
            // Ouvrir le PDF dans un nouvel onglet
            // Fermer ReportConfig et aller vers la liste des rapports
            setTimeout(() => {
              if (onBack) {
                onBack(); // Retour vers le menu principal ou liste des rapports
              }
            }, 500);
            window.open(pdfPath, '_blank');
            
             // Petit délai pour laisser le PDF s'ouvrir
            
            toast.current?.show({
              severity: 'success',
              summary: 'Succès',
              detail: 'Rapport ouvert et vous êtes redirigé vers la liste des rapports',
              life: 3000
            });
          },
          reject: () => {
            // Fermer ReportConfig et aller vers la liste des rapports
            setTimeout(() => {
              if (onBack) {
                onBack(); // Retour vers le menu principal ou liste des rapports
              }
            }, 1000); // Délai pour laisser l'utilisateur lire le message
            
            toast.current?.show({
              severity: 'info',
              summary: 'Rapport sauvegardé',
              detail: 'Vous pouvez retrouver le rapport dans la liste des rapports existants',
              life: 3000
            });
          }
        }))

        // Optionnel : appeler onGenerate du parent si nécessaire
        if (onGenerate) {
          await onGenerate(reportPayload);
        }

      } else {
        console.error('❌ Aucun chemin PDF trouvé dans la réponse');
        console.error('❌ Structure de réponse reçue:', {
          success: apiResponse.success,
          hasResult: !!apiResponse.result,
          resultKeys: apiResponse.result ? Object.keys(apiResponse.result) : 'N/A',
          fullApiResponse: apiResponse
        });
        throw new Error(`Aucun chemin PDF trouvé dans la réponse. Success: ${apiResponse.success}, Result: ${!!apiResponse.result}`);
      }

    } catch (error) {
      console.error('❌ Erreur lors de la génération du rapport:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Erreur lors de la génération du rapport: ' + (error.message || 'Erreur inconnue'),
        life: 5000
      });
    } finally {
      setGenerating(false);
    }
  };

  const calculatePeriodDays = () => {
    if (!formData.startDate || !formData.endDate) return 0;
    return Math.ceil((formData.endDate - formData.startDate) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="min-h-screen surface-100 p-6">
      <Toast ref={toast} />
      
      {/* PageHeader avec titleOlang */}
      <PageHeader 
        titleOlang="report.config"
        onBack={onBack}
      />
      
      {/* Formulaire centré avec largeur réduite et grandes marges */}
      <div className="max-w-2xl mx-auto mt-6 mb-8 px-4">
        <div className="grid">
          {/* Section 1: Titre du Rapport */}
          <div className="col-12 mb-4">
            <Card className="shadow-3 border-round-lg">
              <div className="p-5">
                <div className="flex align-items-center gap-3 mb-4">
                  <div className="flex align-items-center justify-content-center w-3rem h-3rem bg-red-500 text-white border-round-lg text-xl font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-900 m-0 mb-1">Titre du Rapport</h3>
                    <p className="text-sm text-600 m-0">Définissez le titre du rapport</p>
                  </div>
                </div>
                
                <div className="bg-gray-100 p-4 border-round border-2 border-gray-300">
                  <InputText
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Rapport du 02/12/2025"
                    className="w-full bg-white border-2 border-gray-400 text-900 font-bold p-3 border-round"
                  />
                </div>
              </div>
            </Card>
          </div>

          {/* Section 2: Période d'Analyse */}
          <div className="col-12 mb-4">
            <Card className="shadow-3 border-round-lg">
              <div className="p-5">
                <div className="flex align-items-center gap-3 mb-4">
                  <div className="flex align-items-center justify-content-center w-3rem h-3rem bg-orange-500 text-white border-round-lg text-xl font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-900 m-0 mb-1">Période d'Analyse</h3>
                  </div>
                </div>
                
                <div className="bg-gray-100 p-4 border-round border-2 border-gray-300">
                  <div className="grid">
                    <div className="col-12 md:col-6 mb-3">
                      <label className="block text-sm font-bold text-700 mb-2">Date de début</label>
                      <Calendar
                        value={formData.startDate}
                        onChange={(e) => handleInputChange('startDate', e.value)}
                        dateFormat="dd/mm/yy"
                        className="w-full"
                        inputClassName="bg-white border-2 border-gray-400 font-bold p-3"
                        showIcon
                        placeholder="25/11/2025"
                      />
                    </div>
                    
                    <div className="col-12 md:col-6 mb-3">
                      <label className="block text-sm font-bold text-700 mb-2">Date de fin</label>
                      <Calendar
                        value={formData.endDate}
                        onChange={(e) => handleInputChange('endDate', e.value)}
                        dateFormat="dd/mm/yy"
                        className="w-full"
                        inputClassName="bg-white border-2 border-gray-400 font-bold p-3"
                        showIcon
                        placeholder="02/12/2025"
                      />
                    </div>
                  </div>

                  {formData.startDate && formData.endDate && (
                    <div className="mt-3 p-3 bg-blue-50 border-round border-2 border-blue-200 flex align-items-center gap-2">
                      <i className="pi pi-info-circle text-blue-600"></i>
                      <span className="text-sm font-bold text-blue-800">
                        Période sélectionnée : {calculatePeriodDays()} jour(s)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* Section 3: Sélection des Capteurs */}
          <div className="col-12 mb-4">
            <Card className="shadow-3 border-round-lg">
              <div className="p-5">
                <div className="flex align-items-center gap-3 mb-4">
                  <div className="flex align-items-center justify-content-center w-3rem h-3rem bg-red-500 text-white border-round-lg text-xl font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-900 m-0 mb-1">Sélection des Capteurs</h3>
                    <p className="text-sm text-600 m-0">Sélectionnez les capteurs à inclure dans le rapport</p>
                  </div>
                </div>
                
                <div className="bg-gray-100 p-4 border-round border-2 border-gray-300">
                  {loadingCapteurs && (
                    <div className="flex align-items-center gap-2 mt-2">
                      <ProgressSpinner size="small" />
                      <span className="text-sm text-600">Chargement des capteurs...</span>
                    </div>
                  )}
                  {!loadingCapteurs && (
                    <>
                      
                      <MultiSelect
                        value={formData.selectedTags}
                        onChange={(e) => handleInputChange('selectedTags', e.value)}
                        options={tagOptions}
                        optionLabel="label"
                        placeholder="Sélectionnez les capteurs à analyser..."
                        className="w-full"
                        inputClassName="bg-white border-2 border-gray-400 font-bold p-3"
                        display="chip"
                        filter
                        showClear
                      />
                      
                      {formData.selectedTags && formData.selectedTags.length > 0 && (
                        <div className="mt-3 p-3 bg-green-50 border-round border-2 border-green-200">
                          <div className="flex align-items-center gap-2 text-green-700">
                            <i className="pi pi-check-circle"></i>
                            <span className="text-sm font-bold">
                              {formData.selectedTags.length} capteur(s) sélectionné(s)
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* Section 4: Résumé de la Configuration */}
          <div className="col-12 mb-4">
            <Card className="shadow-3 border-round-lg">
              <div className="p-5">
                <div className="flex align-items-center gap-3 mb-4">
                  <div className="flex align-items-center justify-content-center w-3rem h-3rem bg-purple-500 text-white border-round-lg text-xl font-bold">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-900 m-0 mb-1">Résumé de la Configuration</h3>
                  </div>
                </div>
                
                <div className="bg-gray-100 p-4 border-round border-2 border-gray-300">
                  <div className="grid mb-4">
                    <div className="col-12 mb-3">
                      <div className="flex justify-content-between py-2 bg-white p-3 border-round border-2 border-gray-300">
                        <span className="text-600 font-bold">Titre :</span>
                        <span className="font-bold text-900">{formData.title || 'Non défini'}</span>
                      </div>
                    </div>
                    <div className="col-12 mb-3">
                      <div className="flex justify-content-between py-2 bg-white p-3 border-round border-2 border-gray-300">
                        <span className="text-600 font-bold">Période :</span>
                        <span className="font-bold text-900">
                          {formData.startDate && formData.endDate 
                            ? `${formData.startDate.toLocaleDateString('fr-FR')} - ${formData.endDate.toLocaleDateString('fr-FR')}`
                            : 'Non définie'
                          }
                        </span>
                      </div>
                    </div>
                    <div className="col-12 mb-3">
                      <div className="flex justify-content-between py-2 bg-white p-3 border-round border-2 border-gray-300">
                        <span className="text-600 font-bold">Capteurs :</span>
                        <span className="font-bold text-900">
                          {formData.selectedTags?.length || 0} sélectionné(s)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Bouton de génération */}
          <div className="col-12">
            <Button
              label={generating ? 'Génération en cours...' : 'Générer le Rapport'}
              icon={generating ? 'pi pi-spin pi-spinner' : 'pi pi-file-pdf'}
              className="p-button-success w-full p-3 text-lg font-semibold"
              onClick={handleGenerateReport}
              disabled={generating || !formData.title || !formData.startDate || !formData.endDate || !formData.selectedTags?.length}
            />
          </div>
        </div>
      </div>
      
      {/* ConfirmDialog pour le modal de confirmation PDF */}
      <ConfirmDialog />
    </div>
  );
};

export default ReportConfig;
